import { getCookie } from 'cookies-next';
import type { RequestDocument, RequestExtendedOptions } from 'graphql-request';
import { request } from 'graphql-request';

import { graphql } from '@/graphql/__generated/gql';
import { type PlatformRestartResponse } from '@/graphql/__generated/gql/graphql';
import {
    getWindowJwtToken,
    getWindowRefreshJwt,
    resolveRestartSessionJwt,
} from '@/hooks/utils/window/jwtToken';
import { appLogger } from '@/utils/app-logger/appLogger';
import { getAppVersion } from '@/utils/appVersion/getAppVersion';
import { getDeviceModel } from '@/utils/browser/getDeviceModel';
import { DEVICE_ID_COOKIE } from '@/utils/constants/common';
import { isPlaceholderDeviceId } from '@/utils/deviceId';
import { areClientCapabilitiesReady } from '@/utils/session/clientCapabilities';
import {
    markSessionAnonymous,
    markSessionAuthenticated,
    publishSessionJwt,
} from '@/utils/session/publishSessionJwt';
import { takeRestartTicket } from '@/utils/session/restartTicket';
import { getClientTrackingAppName } from '@/utils/tracking/getTrackingAppName';

export const ZONE_APP_SHELL_GRAPHQL_URL = '/api/tn/app-shell/graphql/';
export const platformRestartMutationKey = 'platformRestart';

export const platformRestartMutation = graphql(/* GraphQL */ `
    mutation PlatformRestart($input: PlatformRestartInput!) {
        platformRestart(input: $input) {
            success
            deviceId
            session {
                id
                jwt
            }
            refreshInSec
            errorCode
            error
            message
            status
        }
    }
`);

export type PlatformRestartType = { platformRestart: PlatformRestartResponse };

export type RunPlatformRestartOptions = {
    authoritative?: boolean;
    identity?: 'authenticated' | 'anonymous';
    // Abandons the restart: an aborted response never publishes its JWT or sets its session cookie.
    signal?: AbortSignal;
};

/**
 * Whether the current window state can be recovered by a restart (which refreshes the SAME session and
 * needs an input JWT) rather than a Start (which mints a new one). A published token is a complete
 * session, safe to restart from. The cold-start seed is INCOMPLETE — restarting from it before the
 * DRM/DNT capability cookies exist mints a JWT with null device/DRM claims — so a seed-only restart
 * waits for capabilities. Single source for both this module's seed gate and the recovery ladder.
 */
export function canRestartSession(): boolean {
    if (getWindowJwtToken()) return true;
    return Boolean(getWindowRefreshJwt()) && areClientCapabilitiesReady();
}

/**
 * True when a restart with no explicit override JWT can only draw on the incomplete cold-start seed and
 * capabilities aren't ready yet — restarting would mint a JWT with null device/DRM claims, so skip.
 */
function shouldSkipSeedOnlyRestart(sessionJwtOverride?: string): boolean {
    return !sessionJwtOverride && !canRestartSession();
}

/**
 * Handles a seed-only restart deferred because capabilities aren't ready yet. Keeps the auth marker
 * in sync even though nothing is published: an authoritative sign-in/up arms it (so a later stray
 * anonymous Start can't downgrade the just-authenticated user before the capability-gated completion
 * restart publishes the real token), and an authoritative sign-out clears it (so a just-logged-out
 * user isn't left flagged authenticated, which would misroute recovery to an authenticated restart).
 */
function handleSkippedSeedOnlyRestart(options?: RunPlatformRestartOptions): void {
    if (options?.authoritative && options.identity === 'authenticated') {
        markSessionAuthenticated();
    } else if (options?.authoritative && options.identity === 'anonymous') {
        markSessionAnonymous();
    }
    appLogger.warn(
        'Platform restart skipped; capability cookies not ready for a seed-only restart',
        {
            component: 'runPlatformRestart',
        },
    );
}

/**
 * A restart the platform answered and refused, carrying the answer. `status` is what decides whether
 * the session JWT itself was rejected, which no client-side inspection of the token can establish:
 * a token can be well-formed and unexpired and still be invalid (wrong environment, revoked device,
 * unknown session), and only the platform knows.
 */
export class PlatformRestartError extends Error {
    readonly status?: number;
    readonly errorCode?: string;

    constructor(
        message: string,
        details: { status?: number | null; errorCode?: string | null } = {},
    ) {
        super(message);
        this.name = 'PlatformRestartError';
        this.status = details.status ?? undefined;
        this.errorCode = details.errorCode ?? undefined;
    }
}

/**
 * Error codes for a 400 the platform blamed on our request rather than on the session JWT.
 *
 * Discarding for these would be actively harmful: the token is probably fine, a Start would be sent
 * with the same malformed request and fail the same way, and because a bad parameter affects every
 * restart alike, a single bad deploy would have the whole fleet throwing away working sessions at
 * once. The documented JWT rejection carries no `errorCode` at all, so anything named here is
 * something else.
 *
 * KEEP IN SYNC with the platform restart contract:
 * https://paramount.atlassian.net/wiki/spaces/STC/pages/634815120/Platform+Restart+API+for+Convergence
 * A request-level 400 code the platform adds later and this set does not know would be treated as a
 * JWT rejection fleet-wide. That case is observable: the "Discarded a session JWT" warn carries the
 * `errorCode` that drove it, and a documented rejection has none — alert on that field being set.
 */
const REQUEST_LEVEL_ERROR_CODES = new Set(['INVALID_PARAMETER', 'INVALID_APP_TOKEN']);

/** The platform's answer carried on a thrown restart error, when it was one; empty otherwise. */
export function describePlatformRestartRejection(error: unknown): {
    status?: number;
    errorCode?: string;
} {
    return error instanceof PlatformRestartError
        ? { status: error.status, errorCode: error.errorCode }
        : {};
}

/**
 * Whether the platform refused the session JWT we restarted from, in which case it can never be
 * refreshed and only a Start will produce a usable one.
 *
 * An unrecognised 400 discards the session JWT, which costs one Start, where failing to
 * discard strands the user until they reload the page.
 */
export function isSessionJwtRejectedByPlatform(error: unknown): boolean {
    return (
        error instanceof PlatformRestartError &&
        error.status === 400 &&
        !REQUEST_LEVEL_ERROR_CODES.has(error.errorCode ?? '')
    );
}

/** Builds the thrown message for a failed restart, appending any resolver detail the response carried. */
function describeRestartFailure(
    platformRestart: PlatformRestartResponse | null | undefined,
): string {
    const details = [
        platformRestart?.errorCode ? `errorCode=${platformRestart.errorCode}` : null,
        platformRestart?.message ? `message=${platformRestart.message}` : null,
        platformRestart?.error ? `error=${platformRestart.error}` : null,
        platformRestart?.status != null ? `status=${platformRestart.status}` : null,
    ].filter(Boolean);

    return details.length > 0
        ? `platformRestart: Platform restart failed (${details.join(', ')})`
        : 'platformRestart: Platform restart failed';
}

/**
 * Refreshes the session JWT via the platform restart endpoint and publishes it to window.jwtToken.
 * Used after sign-in/up/out, and by cold-start completion (via `sessionJwtOverride`).
 *
 * @param errors - Accumulator for chained flows (e.g. sign-out); prior steps prepend to the thrown message.
 * @param sessionJwtOverride - JWT to restart from when it isn't on window.jwtToken (cold-start completion).
 * @param options - `authoritative`/`identity` forwarded to `publishSessionJwt` (auth-change flows).
 */
export async function runPlatformRestart(
    errors: string[] = [],
    sessionJwtOverride?: string,
    options?: RunPlatformRestartOptions,
): Promise<PlatformRestartType> {
    const sessionJwt = sessionJwtOverride ?? resolveRestartSessionJwt();

    // Restart needs a JWT to refresh; recovery with none is Start's job. Bail before taking a ticket
    // so a doomed call can't advance the sequence and supersede a concurrent valid restart's publish.
    if (!sessionJwt) {
        // Log here: some callers (sign-in/up) surface the rejection via flow state, not appLogger.
        appLogger.error('Platform restart skipped; no session JWT to restart from', {
            component: 'runPlatformRestart',
        });
        errors.push('platformRestart: no session JWT to restart from');
        throw new Error(errors.join('; '));
    }

    // Gate a seed-only restart on capability readiness so every caller — including the authoritative
    // sign-in/up/out flows — matches useCompleteSessionOnStart and recoverSessionJwt. Skip rather than
    // throw: the auth mutation already succeeded server-side, and the capability-gated completion
    // restart mints the complete authenticated token once the cookies are written.
    if (shouldSkipSeedOnlyRestart(sessionJwtOverride)) {
        handleSkippedSeedOnlyRestart(options);
        return { platformRestart: { success: false } };
    }

    // Restart reuses a valid V2 id only. Missing or all-zero -> send empty. Never the legacy id.
    const existingDeviceId = getCookie(DEVICE_ID_COOKIE) as string | undefined;
    const deviceId =
        existingDeviceId && !isPlaceholderDeviceId(existingDeviceId) ? existingDeviceId : '';
    if (!deviceId) {
        appLogger.warn('Platform restart sending an empty device ID', {
            component: 'runPlatformRestart',
        });
    }
    const ticket = takeRestartTicket();

    const data = (await request({
        url: ZONE_APP_SHELL_GRAPHQL_URL,
        document: platformRestartMutation as RequestDocument,
        variables: {
            input: {
                sessionJwt,
                lastEventTime: String(Date.now()),
                ptvAppName: getClientTrackingAppName(),
                appVersion: getAppVersion(),
                deviceId,
                cmAudienceID: '',
                deviceModel: getDeviceModel(),
            },
        },
        signal: options?.signal as RequestExtendedOptions['signal'],
    })) as PlatformRestartType;

    const platformRestart = data.platformRestart;

    if (!platformRestart || platformRestart.success !== true) {
        errors.push(describeRestartFailure(platformRestart));
        // Typed so the recovery ladder can act on `status` rather than re-parse it out of the message.
        throw new PlatformRestartError(errors.join('; '), {
            status: platformRestart?.status,
            errorCode: platformRestart?.errorCode,
        });
    }

    // Restart never persists the device id it gets back; log if it differs so we can spot the platform
    // handing back a different or all-zero id on refresh.
    appLogger.info('Platform restart response', {
        component: 'runPlatformRestart',
        sentDeviceIdClass: deviceId ? 'valid' : 'empty',
        responseDeviceIdClass: isPlaceholderDeviceId(platformRestart.deviceId ?? undefined)
            ? 'placeholder'
            : platformRestart.deviceId
              ? 'valid'
              : 'absent',
    });

    const nextSessionJwt = platformRestart.session?.jwt;
    if (nextSessionJwt) {
        publishSessionJwt(nextSessionJwt, {
            ticket,
            authoritative: options?.authoritative ?? false,
            identity: options?.identity ?? 'inherit',
        });
        return data;
    }

    // Success but no JWT: leave the window as-is (don't republish the incomplete seed, which would
    // block the Start that mints a complete session) and report it so the Start ladder recovers.
    appLogger.error('Platform restart returned no session JWT', {
        component: 'runPlatformRestart',
    });

    return data;
}
